import { createListCollection, Image } from '@chakra-ui/react';
import { BIF } from '@halo/wallet-app';
import MagicBoxImg from '@/assets/images/luckyBox/MagicBox.png';
import MysticQuestImg from '@/assets/images/luckyBox/MysticQuest.png';

export enum CoverBgType {
  MagicBox = 'MagicBox',
  MysticQuest = 'MysticQuest',
  // Treasure = 'Treasure',
}

export const sendToOptions = createListCollection({
  items: [
    { label: 'Send To 1', value: 'sendTo1' },
    { label: 'Send To 2', value: 'sendTo2' },
    { label: 'Send To 3', value: 'sendTo3' },
  ],
});

export const myAccounts = createListCollection({
  items: [
    { label: 'My Account 1', value: 'myAccount1' },
    { label: 'My Account 2', value: 'myAccount2' },
    { label: 'My Account 3', value: 'myAccount3' },
  ],
});

export const coverOptions = createListCollection({
  items: [
    {
      label: 'chat.magicBox',
      value: CoverBgType.MagicBox,
      icon: <Image width="1em" src={MagicBoxImg} alt="Magic Box" />,
      description: 'Magic Box',
    },
    {
      label: 'chat.mysticQuest',
      value: CoverBgType.MysticQuest,
      icon: <Image width="1em" src={MysticQuestImg} alt="Mystic Quest" />,
      description: 'Mystic Quest',
    },
  ],
});

export interface ComputedLuckyBoxInfo<T = string> {
  expectedAmount: T;
  redpacketAmount: T;
  claimFee: T;
  recipientAtaFee: T;
  totalFeeReserveInUnits: T;
}

export function computedLuckyBoxInfo({
  isMain,
  decimal,
  tokenDecimal,
  exchangeRate,
  redpacketCount,
  amountInTokens,
}: {
  isMain: boolean;
  decimal: number;
  tokenDecimal: number;
  exchangeRate: string;
  redpacketCount: string;
  amountInTokens: string;
}): ComputedLuckyBoxInfo {
  const weiDecimal = new BIF(1).shiftRight(decimal);
  const tokenWeiDecimal = new BIF(1).shiftRight(tokenDecimal);

  const redpacketAmount = new BIF(amountInTokens)
    .mul(isMain ? weiDecimal : tokenWeiDecimal)
    .toString();

  const Transfer_PDA = 10000; //sender to pda transfer gas
  const CREATE_FEE = 5000; //create red packet gas
  const CLAIM_FEE = 5000; //claim gas for once
  const ATA_RENT = 2040000; // create ATA rent for once
  const PDA_RENT = 2234160; //red packet contact rent

  const pdaAtaFee = isMain ? 0 : ATA_RENT;
  const recipientAtaFee = isMain ? '0' : new BIF(redpacketCount).mul(ATA_RENT).toString();
  const claimFeeTotal = new BIF(redpacketCount).mul(CLAIM_FEE);

  const totalFeeReserveLamports = new BIF(Transfer_PDA)
    .sum(CREATE_FEE)
    .sum(claimFeeTotal)
    .sum(pdaAtaFee)
    .sum(recipientAtaFee)
    .sum(PDA_RENT)
    .toString();

  let expectedAmount;
  let totalFeeReserveInUnits;

  if (isMain) {
    totalFeeReserveInUnits = totalFeeReserveLamports;
    expectedAmount = new BIF(redpacketAmount).sum(totalFeeReserveInUnits).toString();
  } else {
    const exchangeRateValue = exchangeRate;
    const gasInMain = new BIF(totalFeeReserveLamports).div(weiDecimal);
    const gasInTokens = gasInMain.div(exchangeRateValue);
    totalFeeReserveInUnits = Math.ceil(
      Number(gasInTokens.mul(tokenWeiDecimal).toString())
    ).toString();

    expectedAmount = new BIF(redpacketAmount).sum(totalFeeReserveInUnits).toString();
  }

  return {
    expectedAmount: new BIF(expectedAmount).div(isMain ? weiDecimal : tokenWeiDecimal).toString(),
    redpacketAmount: new BIF(redpacketAmount)
      .div(isMain ? weiDecimal : tokenWeiDecimal)
      .toString(),
    totalFeeReserveInUnits: new BIF(totalFeeReserveInUnits)
      .div(isMain ? weiDecimal : tokenWeiDecimal)
      .toString(),
    recipientAtaFee: new BIF(recipientAtaFee).div(weiDecimal).toString(),
    claimFee: new BIF(CLAIM_FEE).div(weiDecimal).toString(),
  };
}

export function base64ToUint8Array(base64: string) {
  const binaryString = atob(base64);
  const uint8Array = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i);
  }
  return uint8Array;
}

export function uint8ArrayToBase64(uint8Array: Uint8Array) {
  let binaryString = '';
  const chunkSize = 8192;
  for (let i = 0; i < uint8Array.length; i += chunkSize) {
    const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
    binaryString += String.fromCharCode.apply(null, chunk as any);
  }
  return btoa(binaryString);
}

export function formatTimeDifferenceDetailed(diffInMs: number) {
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);

  const hours = diffInHours;
  const minutes = diffInMinutes % 60;
  const seconds = diffInSeconds % 60;

  const result = [];

  if (hours > 0) {
    result.push(`${hours}h`);
  }

  if (minutes > 0) {
    result.push(`${minutes}m`);
  }

  if (!hours && seconds > 0) {
    result.push(`${seconds}s`);
  }

  if (result.length === 0) {
    return '0s';
  }

  return result.join(' ');
}
